/**
 * Module contract — the Nebraska K-8 math standards as a column tree.
 *
 * Owns: the Nebraska hierarchy in the shape STANDARDS_MAP uses, so the grid
 *       draws either one unchanged: strand -> DomainNode, standard (e.g.
 *       "3.G.2") -> ClusterNode, indicator (e.g. "3.G.2.a") -> StandardNode.
 * Does not own: any mastery data. Only the Broken Bow demo reads it; there is
 *       no live Nebraska data (see service/demoAnalytics).
 *
 * 277 indicators. The seven standards with no indicators (5.D.1, 6.G.2,
 * 6.D.1, 7.N.1, 7.D.2, 8.D.1, 8.D.3) are their own single leaf, labelled with
 * the bare number.
 * Order: grade, then strand N/R/A/G/D, then standard number, then letter.
 */

import type { DomainNode } from "../types/standards";

export const NEBRASKA_STANDARDS_MAP: DomainNode[] = [
  {
    id: "K.N",
    code: "N",
    name: "Number",
    grade: "K",
    clusters: [
      {
        id: "K.N.1",
        letter: "K.N.1",
        short: "Subitizing",
        standards: [
          {
            id: "K.N.1.a",
            s: "1.a",
            desc: 'Without counting, recognize and verbally label arrangements for briefly shown collections up to 10 (e.g., "I saw 5." "How did you know?" "I saw 3 and 2, that is 5."',
          },
        ],
      },
      {
        id: "K.N.2",
        letter: "K.N.2",
        short: "Counting and Cardinality",
        standards: [
          {
            id: "K.N.2.a",
            s: "2.a",
            desc: "Use one-to-one correspondence when counting objects to show the relationship between numbers and quantities and understand the last number counted is a direct representation of the total objects in a given set.",
          },
          {
            id: "K.N.2.b",
            s: "2.b",
            desc: "Understand that each successive number name refers to a quantity that is one larger.",
          },
          {
            id: "K.N.2.c",
            s: "2.c",
            desc: "Count out the number of objects given a number from 1 to 20.",
          },
          {
            id: "K.N.2.d",
            s: "2.d",
            desc: "Count up to 20 objects arranged in a line, a rectangular array, or a circle, and count up to 10 objects in a scattered configuration.",
          },
          {
            id: "K.N.2.e",
            s: "2.e",
            desc: "Count verbally forward and backward from any given number within 20.",
          },
          {
            id: "K.N.2.f",
            s: "2.f",
            desc: "Count verbally in sequential order by ones and by tens to 100, making accurate decade transitions (e.g., 89 to 90).",
          },
          {
            id: "K.N.2.g",
            s: "2.g",
            desc: "Write and name numbers 0 to 20. Represent a number of objects with a written numeral 0 to 20.",
          },
          {
            id: "K.N.2.h",
            s: "2.h",
            desc: "Compare the number of objects in two groups, up to 20, using the words fewer than, more than, the same as.",
          },
        ],
      },
      {
        id: "K.N.3",
        letter: "K.N.3",
        short: "Base Ten",
        standards: [
          {
            id: "K.N.3.a",
            s: "3.a",
            desc: "Compose and decompose numbers from 11 to 19 into a group of ten ones and some more ones using a model, drawing, or equation.",
          },
        ],
      },
      {
        id: "K.N.4",
        letter: "K.N.4",
        short: "Number and Algebraic Relationships",
        standards: [
          {
            id: "K.N.4.a",
            s: "4.a",
            desc: "Represent and explain addition and subtraction as part-whole relationships, with addition as putting together and/or adding to and subtraction as taking apart and/or taking from, using objects, drawings, numbers, and equations.",
          },
          {
            id: "K.N.4.b",
            s: "4.b",
            desc: "Compose and decompose numbers less than or equal to 10 into pairs in more than one way using verbal explanations, objects, or drawings.",
          },
          {
            id: "K.N.4.c",
            s: "4.c",
            desc: "For any number from 1 to 9, find the number that makes 10 when added to the given number, sharing the answer with a model, drawing, or equation.",
          },
          {
            id: "K.N.4.d",
            s: "4.d",
            desc: "Efficiently, flexibly, and accurately add and subtract within 5.",
          },
          {
            id: "K.N.4.e",
            s: "4.e",
            desc: "Solve authentic problems that involve addition and subtraction within 10 (e.g., by using objects, drawings, and equations to represent the problem).",
          },
        ],
      },
    ],
  },
  {
    id: "K.G",
    code: "G",
    name: "Geometry",
    grade: "K",
    clusters: [
      {
        id: "K.G.1",
        letter: "K.G.1",
        short: "Shapes and Their Attributes",
        standards: [
          {
            id: "K.G.1.a",
            s: "1.a",
            desc: "Identify and name two-dimensional shapes including circles, triangles, squares, and rectangles regardless of orientation or size.",
          },
          {
            id: "K.G.1.b",
            s: "1.b",
            desc: "Identify and name three-dimensional shapes including spheres, cubes, cylinders, and cones regardless of orientation or size.",
          },
          {
            id: "K.G.1.c",
            s: "1.c",
            desc: "Describe the relative positions of shapes in relation to other objects or shapes using terms such as above, below, in front of, behind, and next to.",
          },
          {
            id: "K.G.1.d",
            s: "1.d",
            desc: "Create shapes using given materials and describe one or more of the attributes such as number of sides/corners.",
          },
          {
            id: "K.G.1.e",
            s: "1.e",
            desc: "Combine simple shapes to compose larger shapes.",
          },
        ],
      },
      {
        id: "K.G.2",
        letter: "K.G.2",
        short: "Measurement",
        standards: [
          {
            id: "K.G.2.a",
            s: "2.a",
            desc: "Describe measurable attributes of authentic objects including length, capacity, and weight.",
          },
          {
            id: "K.G.2.b",
            s: "2.b",
            desc: "Directly compare two objects with a measurable attribute in common to describe which object is longer/shorter, heavier/lighter, and has more/less-capacity.",
          },
        ],
      },
      {
        id: "K.G.3",
        letter: "K.G.3",
        short: "Time and Money",
        standards: [
          {
            id: "K.G.3.a",
            s: "3.a",
            desc: "Identify the name and value of pennies, nickels, and dimes.",
          },
          {
            id: "K.G.3.b",
            s: "3.b",
            desc: "Identify the parts of digital and analog clocks. Tell and write time to the hour using digital clocks and analog clocks using only the hour hand.",
          },
        ],
      },
    ],
  },
  {
    id: "K.D",
    code: "D",
    name: "Data",
    grade: "K",
    clusters: [
      {
        id: "K.D.1",
        letter: "K.D.1",
        short: "Classification",
        standards: [
          {
            id: "K.D.1.a",
            s: "1.a",
            desc: "Identify, sort, and classify objects by size, shape, color, and other attributes.",
          },
          {
            id: "K.D.1.b",
            s: "1.b",
            desc: "Identify objects that do not belong to a particular group and explain the reasoning used.",
          },
        ],
      },
    ],
  },
  {
    id: "1.N",
    code: "N",
    name: "Number",
    grade: "1",
    clusters: [
      {
        id: "1.N.1",
        letter: "1.N.1",
        short: "Subitizing",
        standards: [
          {
            id: "1.N.1.a",
            s: "1.a",
            desc: 'Without counting, recognize and verbally label arrangements for briefly shown collections up to 20 (e.g.," I saw 16." "How did you know?" "I saw 10 and 6, that is 16").',
          },
        ],
      },
      {
        id: "1.N.2",
        letter: "1.N.2",
        short: "Counting and Cardinality",
        standards: [
          {
            id: "1.N.2.a",
            s: "2.a",
            desc: "Count verbally by ones and tens within 120 starting at any given number.",
          },
          {
            id: "1.N.2.b",
            s: "2.b",
            desc: "Count verbally by ones and tens within 120 starting at any given number. Understand that the given number is a direct representation of the total objects in a given set and counting on each successive number represents adding an additional object, and counting back each proceeding number represents removing an object.",
          },
          {
            id: "1.N.2.c",
            s: "2.c",
            desc: "Write numerals to match a representation of a given set of objects for numbers up to 120.",
          },
          {
            id: "1.N.2.d",
            s: "2.d",
            desc: "Understand patterns of skip counting by 2s, 5s, and 10s.",
          },
        ],
      },
      {
        id: "1.N.3",
        letter: "1.N.3",
        short: "Base Ten",
        standards: [
          {
            id: "1.N.3.a",
            s: "3.a",
            desc: "Understand 10 as a bundle, collection, or (more abstractly) composition of ten ones and that the two digits of a two-digit number represent a composition of some tens and some ones.",
          },
          {
            id: "1.N.3.b",
            s: "3.b",
            desc: "Compare two, two-digit numbers using words greater than, less than, equal to, and symbols <, >, =. Justify comparisons based on the number of tens and ones.",
          },
        ],
      },
      {
        id: "1.N.4",
        letter: "1.N.4",
        short: "Number and Operations",
        standards: [
          {
            id: "1.N.4.a",
            s: "4.a",
            desc: "Add and subtract within 20, using flexible strategies such as counting on or counting back, making ten, using ten, and using doubles and near doubles.",
          },
          {
            id: "1.N.4.b",
            s: "4.b",
            desc: "Efficiently, flexibly, and accurately add and subtract within 10.",
          },
          {
            id: "1.N.4.c",
            s: "4.c",
            desc: "Find the difference between two numbers that are multiples of 10, ranging from 10 to 90 using concrete models, drawings, or strategies, and write the corresponding equation.",
          },
          {
            id: "1.N.4.d",
            s: "4.d",
            desc: "Mentally find 10 more or 10 less than a two-digit number without having to count and explain the reasoning used.",
          },
          {
            id: "1.N.4.e",
            s: "4.e",
            desc: "Add within 100, including adding a two-digit number and a one-digit number, adding a two-digit number and a multiple of ten, using concrete models, drawings, and strategies that reflect an understanding of place value, the relationship between addition and subtraction, and the properties of operations. Relate the strategy to a written method and explain the reasoning used to solve.",
          },
          {
            id: "1.N.4.f",
            s: "4.f",
            desc: "Understand that in adding two-digit numbers, one adds tens and tens, ones and ones; sometimes it is necessary to compose a ten.",
          },
          {
            id: "1.N.4.g",
            s: "4.g",
            desc: "Subtract multiples of ten from two-digit numbers (positive or zero differences) using concrete models, drawings, and strategies that reflect an understanding of place value, the relationship between addition and subtraction, and the properties of operations. Relate the strategy to a written method and explain the reasoning used to solve.",
          },
        ],
      },
      {
        id: "1.N.5",
        letter: "1.N.5",
        short: "Number and Algebraic Relationships",
        standards: [
          {
            id: "1.N.5.a",
            s: "5.a",
            desc: "Use the meaning of the equal sign to determine if equations are true and give examples of equations that are true (e.g., 4 = 4, 6 = 7 - 1, 6 + 3 = 3 + 6, 7 + 2 = 5 + 4).",
          },
          {
            id: "1.N.5.b",
            s: "5.b",
            desc: "Use the relationship of addition and subtraction to solve subtraction problems (e.g., find 12 - 9 = , using the addition fact 9 + 3 = 12).",
          },
          {
            id: "1.N.5.c",
            s: "5.c",
            desc: "Determine the unknown whole number in an addition or subtraction equation (e.g., 7 + ? = 13).",
          },
          {
            id: "1.N.5.d",
            s: "5.d",
            desc: "Use the commutative property of addition to develop addition strategies and compose/decompose numbers to develop addition and subtraction strategies. (See other flexible strategies in 1.N.4.a).",
          },
          {
            id: "1.N.5.e",
            s: "5.e",
            desc: "Solve problems that call for addition of three whole numbers whose sum is less than or equal to 20 using flexible strategies with objects, drawings, and/or equations.",
          },
          {
            id: "1.N.5.f",
            s: "5.f",
            desc: "Solve authentic problems involving addition and subtraction within 20 in situations of adding to, taking from, putting together, taking apart, and comparing, with unknowns in all parts of the addition or subtraction problem by using objects, drawings, and/or equations with a symbol for the unknown number to represent the problem.",
          },
          {
            id: "1.N.5.g",
            s: "5.g",
            desc: "Create an authentic problem to represent a given equation involving addition and subtraction within 20.",
          },
        ],
      },
    ],
  },
  {
    id: "1.G",
    code: "G",
    name: "Geometry",
    grade: "1",
    clusters: [
      {
        id: "1.G.1",
        letter: "1.G.1",
        short: "Shapes and Their Attributes",
        standards: [
          {
            id: "1.G.1.a",
            s: "1.a",
            desc: "Determine geometric attributes of two-dimensional shapes regardless of orientation or size for rhombi, trapezoids, and hexagons (e.g., a hexagon is closed with six sides).",
          },
          {
            id: "1.G.1.b",
            s: "1.b",
            desc: "Determine geometric attributes of three-dimensional shapes including cones, cylinders, cubes, and rectangular prisms regardless of orientation or size.",
          },
          {
            id: "1.G.1.c",
            s: "1.c",
            desc: "Describe lines and sides of shapes as parallel or non-parallel.",
          },
          {
            id: "1.G.1.d",
            s: "1.d",
            desc: "Partition circles and rectangles into two and four equal parts using the language halves and fourths.",
          },
        ],
      },
      {
        id: "1.G.2",
        letter: "1.G.2",
        short: "Measurement",
        standards: [
          {
            id: "1.G.2.a",
            s: "2.a",
            desc: "Measure the length of an object as a whole number of same-size, non-standard units by placing them end to end.",
          },
          {
            id: "1.G.2.b",
            s: "2.b",
            desc: "Order three objects by directly comparing their lengths or indirectly by using a third object.",
          },
        ],
      },
      {
        id: "1.G.3",
        letter: "1.G.3",
        short: "Time and Money",
        standards: [
          {
            id: "1.G.3.a",
            s: "3.a",
            desc: "Understand the value of dimes and pennies (e.g., a dime is equal to ten pennies) relating to tens and ones and solve problems involving dimes and pennies using the ¢ symbol appropriately.",
          },
          {
            id: "1.G.3.b",
            s: "3.b",
            desc: "Count collections of like coins (penny, nickel, and dime) relating to patterns of counting by 1s, 5s, and 10s.",
          },
          {
            id: "1.G.3.c",
            s: "3.c",
            desc: "Tell and write time to the half hour and hour using analog and digital clocks.",
          },
        ],
      },
    ],
  },
  {
    id: "1.D",
    code: "D",
    name: "Data",
    grade: "1",
    clusters: [
      {
        id: "1.D.1",
        letter: "1.D.1",
        short: "Data Collection",
        standards: [
          {
            id: "1.D.1.a",
            s: "1.a",
            desc: "Collect, organize, and represent a data set with up to three categories using a picture graph.",
          },
        ],
      },
      {
        id: "1.D.2",
        letter: "1.D.2",
        short: "Analyze Data and Interpret Results",
        standards: [
          {
            id: "1.D.2.a",
            s: "2.a",
            desc: "Ask and answer questions about the total number of data points, how many in each category, and compare categories by identifying how many more or less are in a particular category using a picture graph.",
          },
        ],
      },
    ],
  },
  {
    id: "2.N",
    code: "N",
    name: "Number",
    grade: "2",
    clusters: [
      {
        id: "2.N.1",
        letter: "2.N.1",
        short: "Subitizing",
        standards: [
          {
            id: "2.N.1.a",
            s: "1.a",
            desc: 'Without counting, recognize and verbally label structured arrangements for briefly shown collections using groups, multiplicative thinking, and place value (e.g.," I saw 48." "How did you know?" "I saw 4 groups of 10 and 2 groups of 4 is 8...4 tens and 8 ones...48").',
          },
        ],
      },
      {
        id: "2.N.2",
        letter: "2.N.2",
        short: "Counting",
        standards: [
          {
            id: "2.N.2.a",
            s: "2.a",
            desc: "Count within 1,000, including skip counting by 5s, 10s, and 100s starting at a variety of multiples of 5, 10, or 100.",
          },
        ],
      },
      {
        id: "2.N.3",
        letter: "2.N.3",
        short: "Base Ten",
        standards: [
          {
            id: "2.N.3.a",
            s: "3.a",
            desc: "Read and write numbers within the range of 0 to 1,000 using standard, word, and expanded forms.",
          },
          {
            id: "2.N.3.b",
            s: "3.b",
            desc: "Understand 100 as a bundle, collection, or (more abstractly) composition of ten tens and that the three digits of a three-digit number represent a composition of some hundreds, some tens, and some ones.",
          },
          {
            id: "2.N.3.c",
            s: "3.c",
            desc: "Compare two three-digit numbers by using symbols <, >, = and justify the comparison based on the value of the hundreds, tens, and ones.",
          },
        ],
      },
      {
        id: "2.N.4",
        letter: "2.N.4",
        short: "Number and Operations",
        standards: [
          {
            id: "2.N.4.a",
            s: "4.a",
            desc: "Fluently add and subtract within 20.",
          },
          {
            id: "2.N.4.b",
            s: "4.b",
            desc: "Add and subtract using 100 strategies based on place value including properties of operations, relationships between addition and subtraction, and algorithms.",
          },
          {
            id: "2.N.4.c",
            s: "4.c",
            desc: "Mentally add or subtract 10 or 100 to or from a given number 100 to 900.",
          },
          {
            id: "2.N.4.d",
            s: "4.d",
            desc: "Add up to three two-digit numbers using strategies based on place value and understanding of properties.",
          },
          {
            id: "2.N.4.e",
            s: "4.e",
            desc: "Add and subtract within 1,000 using concrete models, drawings, and strategies that reflect an understanding of place value and the properties of operations.",
          },
        ],
      },
      {
        id: "2.N.5",
        letter: "2.N.5",
        short: "Number and Algebraic Relationships",
        standards: [
          {
            id: "2.N.5.a",
            s: "5.a",
            desc: "Solve authentic problems involving addition and subtraction within 100 in situations of addition and subtraction, including adding to, subtracting from, joining and separating, and comparing situations with unknowns in all positions using objects, models, drawings, verbal explanations, expressions, and equations.",
          },
          {
            id: "2.N.5.b",
            s: "5.b",
            desc: "Create authentic problems to represent one-step addition and subtraction within 100 with unknowns in all positions.",
          },
          {
            id: "2.N.5.c",
            s: "5.c",
            desc: "Use repeated addition to find the total number of objects arranged in an array no larger than five rows and five columns and write an equation to express the total.",
          },
          {
            id: "2.N.5.d",
            s: "5.d",
            desc: "Identify a group of objects from 0 to 20 as even or odd by counting by 2s or by showing even numbers as a sum of two equal parts.",
          },
        ],
      },
    ],
  },
  {
    id: "2.G",
    code: "G",
    name: "Geometry",
    grade: "2",
    clusters: [
      {
        id: "2.G.1",
        letter: "2.G.1",
        short: "Shapes and Their Attributes",
        standards: [
          {
            id: "2.G.1.a",
            s: "1.a",
            desc: "Recognize and describe all faces of three-dimensional shapes as two-dimensional shapes. Identify and count attributes of solid shapes including the edges, faces, and vertices.",
          },
          {
            id: "2.G.1.b",
            s: "1.b",
            desc: "Recognize and draw two-dimensional shapes having a specific number of sides, angles, and vertices including triangles, quadrilaterals, pentagons, and hexagons.",
          },
          {
            id: "2.G.1.c",
            s: "1.c",
            desc: "Partition a rectangle into rows and columns of equal-sized squares and count to find the total.",
          },
          {
            id: "2.G.1.d",
            s: "1.d",
            desc: "Divide circles and rectangles into two, three, or four equal parts and describe the parts using the language of halves, thirds, fourths, half of, a third of, and a fourth of.",
          },
          {
            id: "2.G.1.e",
            s: "1.e",
            desc: "Recognize that equal shares of identical wholes need not have the same shape.",
          },
        ],
      },
      {
        id: "2.G.2",
        letter: "2.G.2",
        short: "Describe Measurable Attributes",
        standards: [
          {
            id: "2.G.2.a",
            s: "2.a",
            desc: "Measure the length of an object using two different length units and describe how the measurements relate to the size of the specific unit.",
          },
          {
            id: "2.G.2.b",
            s: "2.b",
            desc: "Compare the difference in length of objects using inches and feet or centimeters and meters.",
          },
        ],
      },
      {
        id: "2.G.3",
        letter: "2.G.3",
        short: "Measurement",
        standards: [
          {
            id: "2.G.3.a",
            s: "3.a",
            desc: "Identify and use appropriate tools for measuring length.",
          },
          {
            id: "2.G.3.b",
            s: "3.b",
            desc: "Measure and estimate lengths using whole numbers with inches, feet, centimeters, and meters.",
          },
        ],
      },
      {
        id: "2.G.4",
        letter: "2.G.4",
        short: "Relate Addition and Subtraction to Measurement",
        standards: [
          {
            id: "2.G.4.a",
            s: "4.a",
            desc: "Represent whole numbers as equally spaced lengths on a number line diagram. Use number lines to find sums and differences within 100.",
          },
          {
            id: "2.G.4.b",
            s: "4.b",
            desc: "Use addition and subtraction within 100 to solve problems using the same standard-length units.",
          },
        ],
      },
      {
        id: "2.G.5",
        letter: "2.G.5",
        short: "Time and Money",
        standards: [
          {
            id: "2.G.5.a",
            s: "5.a",
            desc: "Solve problems involving dollar bills, quarters, dimes, nickels, and pennies using $ and ¢ symbols appropriately.",
          },
          {
            id: "2.G.5.b",
            s: "5.b",
            desc: "Identify and write time to five-minute intervals using analog and digital clocks and both a.m. and p.m.",
          },
        ],
      },
    ],
  },
  {
    id: "2.D",
    code: "D",
    name: "Data",
    grade: "2",
    clusters: [
      {
        id: "2.D.1",
        letter: "2.D.1",
        short: "Data Collection",
        standards: [
          {
            id: "2.D.1.a",
            s: "1.a",
            desc: "Ask authentic questions to generate data and represent the data using scaled picture graphs with up to four categories.",
          },
          {
            id: "2.D.1.b",
            s: "1.b",
            desc: "Ask authentic questions to generate data and represent the data using bar graphs with up to four categories.",
          },
          {
            id: "2.D.1.c",
            s: "1.c",
            desc: "Create and represent a data set by making a line plot using whole numbers.",
          },
        ],
      },
      {
        id: "2.D.2",
        letter: "2.D.2",
        short: "Analyze Data and Interpret Results",
        standards: [
          {
            id: "2.D.2.a",
            s: "2.a",
            desc: "Analyze data using scaled picture graphs or bar graphs with up to four categories. Solve problems including one-step comparison problems, using information from the graphs.",
          },
        ],
      },
    ],
  },
  {
    id: "3.N",
    code: "N",
    name: "Number",
    grade: "3",
    clusters: [
      {
        id: "3.N.1",
        letter: "3.N.1",
        short: "Numeric Relationships",
        standards: [
          {
            id: "3.N.1.a",
            s: "1.a",
            desc: "Read, write, and demonstrate multiple equivalent representations for numbers up to 10,000 using objects or visual representations including standard form and expanded form.",
          },
          {
            id: "3.N.1.b",
            s: "1.b",
            desc: "Represent and justify comparisons of whole numbers up to 10,000 using number lines and reasoning strategies.",
          },
        ],
      },
      {
        id: "3.N.2",
        letter: "3.N.2",
        short: "Fractions",
        standards: [
          {
            id: "3.N.2.a",
            s: "2.a",
            desc: "Partition two-dimensional figures into equal areas and express the area of each part as a unit fraction of the whole.",
          },
          {
            id: "3.N.2.b",
            s: "2.b",
            desc: "Find parts of a whole using visual fraction models.",
          },
          {
            id: "3.N.2.c",
            s: "2.c",
            desc: "Represent and understand a fraction as a number on a number line.",
          },
          {
            id: "3.N.2.d",
            s: "2.d",
            desc: "Show and identify equivalent fractions using visual representations including pictures, manipulatives, and number lines.",
          },
          {
            id: "3.N.2.e",
            s: "2.e",
            desc: "Justify whole numbers as fractions and identify fractions that are equivalent to whole numbers.",
          },
          {
            id: "3.N.2.f",
            s: "2.f",
            desc: "Compare and order fractions having the same numerators or denominators by reasoning about their size.",
          },
        ],
      },
    ],
  },
  {
    id: "3.A",
    code: "A",
    name: "Algebra",
    grade: "3",
    clusters: [
      {
        id: "3.A.1",
        letter: "3.A.1",
        short: "Operations and Algebraic Thinking",
        standards: [
          {
            id: "3.A.1.a",
            s: "1.a",
            desc: "Add and subtract up to four-digit whole numbers with or without regrouping using strategies based on place value and algorithms.",
          },
          {
            id: "3.A.1.b",
            s: "1.b",
            desc: "Determine the reasonableness of whole number sums and differences using estimations and number sense.",
          },
          {
            id: "3.A.1.c",
            s: "1.c",
            desc: "Solve and write one-step whole number equations to represent authentic problems using the four operations including equations with an unknown start, unknown change, or unknown result.",
          },
          {
            id: "3.A.1.d",
            s: "1.d",
            desc: "Interpret and solve two-step authentic problems involving whole numbers and the four operations.",
          },
          {
            id: "3.A.1.e",
            s: "1.e",
            desc: "Apply commutative, associative, distributive, identity, and zero properties as strategies to multiply and divide.",
          },
          {
            id: "3.A.1.f",
            s: "1.f",
            desc: "Use drawings, words, arrays, symbols, repeated addition, equal groups, and number lines to interpret and explain the meaning of multiplication and division and their relationship.",
          },
          {
            id: "3.A.1.g",
            s: "1.g",
            desc: "Fluently multiply and divide within 100 using strategies based on understanding and properties of operations.",
          },
          {
            id: "3.A.1.h",
            s: "1.h",
            desc: "Multiply one-digit whole numbers by multiples of 10 in the range of 10 to 90 using strategies based on place value and properties of operations.",
          },
        ],
      },
    ],
  },
  {
    id: "3.G",
    code: "G",
    name: "Geometry",
    grade: "3",
    clusters: [
      {
        id: "3.G.1",
        letter: "3.G.1",
        short: "Shapes and Their Attributes",
        standards: [
          {
            id: "3.G.1.a",
            s: "1.a",
            desc: "Sort quadrilaterals into categories according to their attributes.",
          },
        ],
      },
      {
        id: "3.G.2",
        letter: "3.G.2",
        short: "Area and Perimeter",
        standards: [
          {
            id: "3.G.2.a",
            s: "2.a",
            desc: "Solve authentic problems involving perimeters of polygons when given the side lengths or when given the perimeter and unknown side length(s).",
          },
          {
            id: "3.G.2.b",
            s: "2.b",
            desc: "Use concrete and pictorial models to measure areas in square units by counting square units.",
          },
          {
            id: "3.G.2.c",
            s: "2.c",
            desc: "Find the area of a rectangle with whole-number side lengths by modeling with unit squares; show that area can be additive and is the same as would be found by multiplying the side lengths.",
          },
        ],
      },
      {
        id: "3.G.3",
        letter: "3.G.3",
        short: "Measurement",
        standards: [
          {
            id: "3.G.3.a",
            s: "3.a",
            desc: "Identify and use the appropriate tools and units of measurement, both customary and metric, to solve authentic problems involving length, weight, mass, liquid volume, and capacity (within the same system and unit).",
          },
          {
            id: "3.G.3.b",
            s: "3.b",
            desc: "Estimate and measure length to the nearest half inch, fourth inch, and centimeter.",
          },
        ],
      },
      {
        id: "3.G.4",
        letter: "3.G.4",
        short: "Time",
        standards: [
          {
            id: "3.G.4.a",
            s: "4.a",
            desc: "Tell and write time to the minute using both analog and digital clocks.",
          },
          {
            id: "3.G.4.b",
            s: "4.b",
            desc: "Solve authentic problems involving addition and subtraction of time intervals and find elapsed time.",
          },
        ],
      },
    ],
  },
  {
    id: "3.D",
    code: "D",
    name: "Data",
    grade: "3",
    clusters: [
      {
        id: "3.D.1",
        letter: "3.D.1",
        short: "Data Collection",
        standards: [
          {
            id: "3.D.1.a",
            s: "1.a",
            desc: "Create scaled picture graphs and scaled bar graphs to represent a data set with more than four categories, including data collected through observations, surveys, and experiments.",
          },
          {
            id: "3.D.1.b",
            s: "1.b",
            desc: "Generate and represent data using line plots where the horizontal scale is marked off in halves and whole number units.",
          },
        ],
      },
      {
        id: "3.D.2",
        letter: "3.D.2",
        short: "Analyze Data and Interpret Results",
        standards: [
          {
            id: "3.D.2.a",
            s: "2.a",
            desc: "Analyze data and make simple statements using information represented in picture graphs, line plots, and bar graphs.",
          },
        ],
      },
    ],
  },
  {
    id: "4.N",
    code: "N",
    name: "Number",
    grade: "4",
    clusters: [
      {
        id: "4.N.1",
        letter: "4.N.1",
        short: "Numeric Relationships",
        standards: [
          {
            id: "4.N.1.a",
            s: "1.a",
            desc: "Read, write, and demonstrate multiple equivalent representations for whole numbers up to 1,000,000 and decimals to the hundredths using visual representations, standard form, and expanded form.",
          },
          {
            id: "4.N.1.b",
            s: "1.b",
            desc: "Represent and justify comparisons of whole numbers up to 1,000,000 and decimals through the hundredths place using number lines and reasoning strategies.",
          },
          {
            id: "4.N.1.c",
            s: "1.c",
            desc: "Recognize a digit in one place represents ten times what it represents in the place to its right.",
          },
          {
            id: "4.N.1.d",
            s: "1.d",
            desc: "Use decimal notation for fractions with denominators of 10 or 100 (e.g., 43/100 = 0.43).",
          },
        ],
      },
      {
        id: "4.N.2",
        letter: "4.N.2",
        short: "Fractions and Decimals",
        standards: [
          {
            id: "4.N.2.a",
            s: "2.a",
            desc: "Explain and demonstrate how a mixed number is equivalent to a fraction greater than one and how a fraction greater than one is equivalent to a mixed number using visual fraction models and reasoning strategies.",
          },
          {
            id: "4.N.2.b",
            s: "2.b",
            desc: "Explain and demonstrate how equivalent fractions are generated by multiplying by a fraction equivalent to 1 using visual fraction models and the Identity Property of Multiplication.",
          },
          {
            id: "4.N.2.c",
            s: "2.c",
            desc: "Compare and order fractions having unlike numerators or denominators using number lines, benchmarks, reasoning strategies, and/or equivalence.",
          },
        ],
      },
      {
        id: "4.N.3",
        letter: "4.N.3",
        short: "Operations with Fractions",
        standards: [
          {
            id: "4.N.3.a",
            s: "3.a",
            desc: "Decompose a fraction into a sum of fractions with the same denominator in more than one way and record each decomposition with an equation and a visual representation.",
          },
          {
            id: "4.N.3.b",
            s: "3.b",
            desc: "Explain the meaning of addition and subtraction of fractions with like denominators using visual fraction models, properties of operations, and reasoning strategies.",
          },
          {
            id: "4.N.3.c",
            s: "3.c",
            desc: "Add and subtract fractions and mixed numbers with like denominators.",
          },
          {
            id: "4.N.3.d",
            s: "3.d",
            desc: "Solve authentic problems involving addition and subtraction of fractions and mixed numbers with like denominators.",
          },
          {
            id: "4.N.3.e",
            s: "3.e",
            desc: "Multiply a fraction by a whole number using visual fraction models and properties of operations.",
          },
        ],
      },
      {
        id: "4.N.4",
        letter: "4.N.4",
        short: "Factors and Multiples",
        standards: [
          {
            id: "4.N.4.a",
            s: "4.a",
            desc: "Determine whether a given whole number up to 100 is a multiple of a given one-digit number.",
          },
          {
            id: "4.N.4.b",
            s: "4.b",
            desc: "Determine factors of any whole number up to 100 and classify a number up to 100 as prime or composite.",
          },
        ],
      },
    ],
  },
  {
    id: "4.A",
    code: "A",
    name: "Algebra",
    grade: "4",
    clusters: [
      {
        id: "4.A.1",
        letter: "4.A.1",
        short: "Operations and Algebraic Thinking",
        standards: [
          {
            id: "4.A.1.a",
            s: "1.a",
            desc: "Add and subtract multi-digit numbers using an algorithm.",
          },
          {
            id: "4.A.1.b",
            s: "1.b",
            desc: "Multiply up to a four-digit whole number by a one-digit whole number and multiply a two-digit whole number by a two-digit whole number, using strategies based on place value, properties of operations, and algorithms.",
          },
          {
            id: "4.A.1.c",
            s: "1.c",
            desc: "Divide up to a four-digit whole number by a one-digit divisor with and without a remainder using strategies based on place value.",
          },
          {
            id: "4.A.1.d",
            s: "1.d",
            desc: "Determine the reasonableness of whole number products and quotients using estimations and number sense.",
          },
          {
            id: "4.A.1.e",
            s: "1.e",
            desc: "Create a simple algebraic expression or equation using a variable for an unknown number to represent an authentic mathematical situation (e.g., 3 + n = 15, 81 ÷ n = 9).",
          },
          {
            id: "4.A.1.f",
            s: "1.f",
            desc: "Solve one- and two-step authentic problems using the four operations including interpreting remainders and the use of a letter to represent the unknown quantity.",
          },
        ],
      },
    ],
  },
  {
    id: "4.G",
    code: "G",
    name: "Geometry",
    grade: "4",
    clusters: [
      {
        id: "4.G.1",
        letter: "4.G.1",
        short: "Shapes and Their Attributes",
        standards: [
          {
            id: "4.G.1.a",
            s: "1.a",
            desc: "Identify, create, and describe points, lines, line segments, rays, angles, parallel lines, perpendicular lines, and intersecting lines.",
          },
          {
            id: "4.G.1.b",
            s: "1.b",
            desc: "Justify the classification of angles as acute, obtuse, or right.",
          },
          {
            id: "4.G.1.c",
            s: "1.c",
            desc: "Justify the classification of two-dimensional shapes based on the presence or absence of parallel and perpendicular lines or the presence or absence of specific angles.",
          },
          {
            id: "4.G.1.d",
            s: "1.d",
            desc: "Recognize, draw, and justify lines of symmetry in two-dimensional shapes.",
          },
        ],
      },
      {
        id: "4.G.2",
        letter: "4.G.2",
        short: "Measurement",
        standards: [
          {
            id: "4.G.2.a",
            s: "2.a",
            desc: "Identify and use the appropriate tools, operations, and units of measurement, both customary and metric, to solve authentic problems involving time, length, weight, mass, and capacity.",
          },
          {
            id: "4.G.2.b",
            s: "2.b",
            desc: "Determine the reasonableness of measurements involving time, length, weight, mass, capacity, and angles.",
          },
          {
            id: "4.G.2.c",
            s: "2.c",
            desc: "Generate simple conversions from a larger unit to a smaller unit within the customary and metric systems of measurement.",
          },
          {
            id: "4.G.2.d",
            s: "2.d",
            desc: "Measure angles in whole number degrees using a protractor and relate benchmark angle measurements to their rotation through a circle (e.g., 180º = 1/2 of a circle).",
          },
          {
            id: "4.G.2.e",
            s: "2.e",
            desc: "Recognize angle measures as additive and solve problems involving addition and subtraction to find unknown angles on a diagram.",
          },
        ],
      },
      {
        id: "4.G.3",
        letter: "4.G.3",
        short: "Area and Perimeter",
        standards: [
          {
            id: "4.G.3.a",
            s: "3.a",
            desc: "Apply perimeter and area formulas for rectangles to solve authentic problems.",
          },
        ],
      },
    ],
  },
  {
    id: "4.D",
    code: "D",
    name: "Data",
    grade: "4",
    clusters: [
      {
        id: "4.D.1",
        letter: "4.D.1",
        short: "Data Collection",
        standards: [
          {
            id: "4.D.1.a",
            s: "1.a",
            desc: "Generate and represent data using line plots where the horizontal scale is marked off in appropriate units-whole numbers, halves, fourths, or eighths.",
          },
        ],
      },
      {
        id: "4.D.2",
        letter: "4.D.2",
        short: "Analyze Data and Interpret Results",
        standards: [
          {
            id: "4.D.2.a",
            s: "2.a",
            desc: "Solve authentic problems and analyze data involving addition or subtraction of fractions presented in line plots.",
          },
        ],
      },
    ],
  },
  {
    id: "5.N",
    code: "N",
    name: "Number",
    grade: "5",
    clusters: [
      {
        id: "5.N.1",
        letter: "5.N.1",
        short: "Numeric Relationships",
        standards: [
          {
            id: "5.N.1.a",
            s: "1.a",
            desc: "Read, write, and demonstrate multiple equivalent representations for multi-digit whole numbers and decimals through the thousandths place using standard form and expanded form.",
          },
          {
            id: "5.N.1.b",
            s: "1.b",
            desc: "Recognize a digit in one place represents 1/10 of what it represents in the place to its left.",
          },
          {
            id: "5.N.1.c",
            s: "1.c",
            desc: "Use whole number exponents to denote powers of 10.",
          },
        ],
      },
      {
        id: "5.N.2",
        letter: "5.N.2",
        short: "Fractions and Decimals",
        standards: [
          {
            id: "5.N.2.a",
            s: "2.a",
            desc: "Generate equivalent forms of commonly used fractions and decimals (e.g., halves, fourths, fifths, tenths).",
          },
          {
            id: "5.N.2.b",
            s: "2.b",
            desc: "Represent and justify comparisons of whole numbers, fractions, mixed numbers, and decimals through the thousandths place using number lines, reasoning strategies, and/or equivalence.",
          },
        ],
      },
      {
        id: "5.N.3",
        letter: "5.N.3",
        short: "Operations with Fractions and Decimals",
        standards: [
          {
            id: "5.N.3.a",
            s: "3.a",
            desc: "Interpret a fraction as division of the numerator by the denominator.",
          },
          {
            id: "5.N.3.b",
            s: "3.b",
            desc: "Multiply a whole number by a fraction or a fraction by a fraction, including mixed numbers, using visual fraction models and properties of operations.",
          },
          {
            id: "5.N.3.c",
            s: "3.c",
            desc: "Divide a unit fraction by a whole number and a whole number by a unit fraction using visual fraction models and properties of operations.",
          },
          {
            id: "5.N.3.d",
            s: "3.d",
            desc: "Solve authentic problems involving addition, subtraction, and multiplication of fractions and mixed numbers with like and unlike denominators.",
          },
          {
            id: "5.N.3.e",
            s: "3.e",
            desc: "Add and subtract fractions and mixed numbers with unlike denominators without simplifying.",
          },
          {
            id: "5.N.3.f",
            s: "3.f",
            desc: "Solve authentic problems involving division of unit fractions by whole numbers and division of whole numbers by unit fractions.",
          },
          {
            id: "5.N.3.g",
            s: "3.g",
            desc: "Add, subtract, multiply, and divide decimals to hundredths using strategies based on place value, properties of operations, and/or algorithms.",
          },
        ],
      },
    ],
  },
  {
    id: "5.A",
    code: "A",
    name: "Algebra",
    grade: "5",
    clusters: [
      {
        id: "5.A.1",
        letter: "5.A.1",
        short: "Operations and Algebraic Thinking",
        standards: [
          {
            id: "5.A.1.a",
            s: "1.a",
            desc: "Multiply multi-digit whole numbers using an algorithm.",
          },
          {
            id: "5.A.1.b",
            s: "1.b",
            desc: "Divide four-digit whole numbers by a two-digit divisor, with and without remainders, using strategies based on place value.",
          },
          {
            id: "5.A.1.c",
            s: "1.c",
            desc: "Justify the reasonableness of computations involving whole numbers, fractions, and decimals.",
          },
          {
            id: "5.A.1.d",
            s: "1.d",
            desc: "Simplify authentic numerical or algebraic expressions using order of operations (excluding exponents).",
          },
        ],
      },
    ],
  },
  {
    id: "5.G",
    code: "G",
    name: "Geometry",
    grade: "5",
    clusters: [
      {
        id: "5.G.1",
        letter: "5.G.1",
        short: "Shapes and Their Attributes",
        standards: [
          {
            id: "5.G.1.a",
            s: "1.a",
            desc: "Identify and describe faces, edges, and vertices of rectangular prisms.",
          },
          {
            id: "5.G.1.b",
            s: "1.b",
            desc: "Recognize volume as an attribute of solid figures that is measured in cubic units.",
          },
          {
            id: "5.G.1.c",
            s: "1.c",
            desc: "Justify the classification of two and three-dimensional figures in a hierarchy based on their properties.",
          },
        ],
      },
      {
        id: "5.G.2",
        letter: "5.G.2",
        short: "Coordinate Geometry",
        standards: [
          {
            id: "5.G.2.a",
            s: "2.a",
            desc: "Identify the origin, x axis, and y axis of the coordinate plane.",
          },
          {
            id: "5.G.2.b",
            s: "2.b",
            desc: "Graph and name points in the first quadrant of the coordinate plane using ordered pairs of whole numbers.",
          },
          {
            id: "5.G.2.c",
            s: "2.c",
            desc: "Form ordered pairs from authentic problems involving rules or patterns, graph the ordered pairs in the first quadrant on a coordinate plane, and interpret coordinate values in the context of the situation.",
          },
        ],
      },
      {
        id: "5.G.3",
        letter: "5.G.3",
        short: "Measurement",
        standards: [
          {
            id: "5.G.3.a",
            s: "3.a",
            desc: "Generate conversions in authentic mathematical situations from larger units to smaller units and smaller units to larger units, within the customary and metric systems of measurement.",
          },
        ],
      },
      {
        id: "5.G.4",
        letter: "5.G.4",
        short: "Area and Volume",
        standards: [
          {
            id: "5.G.4.a",
            s: "4.a",
            desc: "Find the area of a rectangle with fractional side lengths by tiling it with unit squares of the fraction side lengths and show that the area is the same as would be found by multiplying the side lengths.",
          },
          {
            id: "5.G.4.b",
            s: "4.b",
            desc: "Multiply fractional side lengths to find areas of rectangles and represent fraction products as rectangular areas.",
          },
          {
            id: "5.G.4.c",
            s: "4.c",
            desc: "Use concrete models to measure the volume of rectangular prisms by counting cubic units.",
          },
          {
            id: "5.G.4.d",
            s: "4.d",
            desc: "Find the volume of a rectangular prism with whole-number side lengths by modeling with unit cubes and show that the volume can be additive and is the same as would be found by multiplying the area of the base times height.",
          },
          {
            id: "5.G.4.e",
            s: "4.e",
            desc: "Solve authentic problems by applying the formulas V = l × w × h and V = B × h for rectangular prisms to find volumes of rectangular prisms with whole number edge lengths.",
          },
        ],
      },
    ],
  },
  {
    id: "5.D",
    code: "D",
    name: "Data",
    grade: "5",
    clusters: [
      {
        id: "5.D.1",
        letter: "5.D.1",
        short: "Data Collection",
        standards: [
          {
            id: "5.D.1",
            s: "1",
            desc: "Students will formulate questions to collect, organize, and represent data.",
          },
        ],
      },
      {
        id: "5.D.2",
        letter: "5.D.2",
        short: "Analyze Data and Interpret Results",
        standards: [
          {
            id: "5.D.2.a",
            s: "2.a",
            desc: "Represent, analyze, and solve authentic problems using information presented in one or more tables or line plots including whole numbers and fractions.",
          },
        ],
      },
    ],
  },
  {
    id: "6.N",
    code: "N",
    name: "Number",
    grade: "6",
    clusters: [
      {
        id: "6.N.1",
        letter: "6.N.1",
        short: "Numeric Relationships",
        standards: [
          {
            id: "6.N.1.a",
            s: "1.a",
            desc: "Determine common factors and common multiples.",
          },
          {
            id: "6.N.1.b",
            s: "1.b",
            desc: "Determine prime factorization of numbers with and without exponents.",
          },
          {
            id: "6.N.1.c",
            s: "1.c",
            desc: "Model integers using drawings, words, number lines, models, and symbols.",
          },
          {
            id: "6.N.1.d",
            s: "1.d",
            desc: "Determine absolute value of rational numbers.",
          },
          {
            id: "6.N.1.e",
            s: "1.e",
            desc: "Compare and order numbers including non-negative fractions and decimals, integers, and absolute values and locate them on the number line.",
          },
        ],
      },
      {
        id: "6.N.2",
        letter: "6.N.2",
        short: "Operations",
        standards: [
          {
            id: "6.N.2.a",
            s: "2.a",
            desc: "Divide multi-digit whole numbers and decimals using an algorithm.",
          },
          {
            id: "6.N.2.b",
            s: "2.b",
            desc: "Divide non-negative fractions and mixed numbers.",
          },
          {
            id: "6.N.2.c",
            s: "2.c",
            desc: "Evaluate numerical expressions including absolute value and/or positive exponents with respect to order of operations.",
          },
        ],
      },
    ],
  },
  {
    id: "6.R",
    code: "R",
    name: "Ratios and Proportions",
    grade: "6",
    clusters: [
      {
        id: "6.R.1",
        letter: "6.R.1",
        short: "Ratios and Rates",
        standards: [
          {
            id: "6.R.1.a",
            s: "1.a",
            desc: "Determine ratios from concrete models, drawings, and/or words.",
          },
          {
            id: "6.R.1.b",
            s: "1.b",
            desc: "Explain and determine unit rates.",
          },
          {
            id: "6.R.1.c",
            s: "1.c",
            desc: "Find a percent of a quantity as a rate per 100 and solve problems involving finding the whole, given a part and the percent.",
          },
          {
            id: "6.R.1.d",
            s: "1.d",
            desc: "Convert among fractions, decimals, and percents using multiple representations.",
          },
          {
            id: "6.R.1.e",
            s: "1.e",
            desc: "Solve authentic problems using ratios, unit rates, and percents.",
          },
          {
            id: "6.R.1.f",
            s: "1.f",
            desc: "Use ratio reasoning to convert measurement units; manipulate and transform units appropriately when multiplying or dividing quantities.",
          },
        ],
      },
      {
        id: "6.R.2",
        letter: "6.R.2",
        short: "Represent",
        standards: [
          {
            id: "6.R.2.a",
            s: "2.a",
            desc: "Identify the ordered pair of a given point in the coordinate plane.",
          },
          {
            id: "6.R.2.b",
            s: "2.b",
            desc: "Plot the location of an ordered pair in the coordinate plane.",
          },
          {
            id: "6.R.2.c",
            s: "2.c",
            desc: "Identify the location of a given point in the coordinate plane (e.g., axis, origin, quadrant).",
          },
          {
            id: "6.R.2.d",
            s: "2.d",
            desc: "Make tables of equivalent ratios relating quantities with whole number measurements.",
          },
          {
            id: "6.R.2.e",
            s: "2.e",
            desc: "Use the constant of proportionality to find the missing value in ratio tables.",
          },
          {
            id: "6.R.2.f",
            s: "2.f",
            desc: "Plot the pair of values from a ratio table on the coordinate plane.",
          },
          {
            id: "6.R.2.g",
            s: "2.g",
            desc: "Explain what a point (x, y) on the graph of a proportional relationship means in terms of the situation.",
          },
        ],
      },
    ],
  },
  {
    id: "6.A",
    code: "A",
    name: "Algebra",
    grade: "6",
    clusters: [
      {
        id: "6.A.1",
        letter: "6.A.1",
        short: "Algebraic Processes",
        standards: [
          {
            id: "6.A.1.a",
            s: "1.a",
            desc: "Recognize and generate equivalent algebraic expressions involving the distributive property and combining like terms.",
          },
          {
            id: "6.A.1.b",
            s: "1.b",
            desc: "Given the value of the variable, evaluate algebraic expressions with non-negative rational numbers with respect to order of operations, which may include absolute value.",
          },
          {
            id: "6.A.1.c",
            s: "1.c",
            desc: "Use substitution to determine if a given value for a variable makes an equation or inequality true.",
          },
          {
            id: "6.A.1.d",
            s: "1.d",
            desc: "Solve one-step equations with non-negative rational numbers using addition, subtraction, multiplication, and division.",
          },
          {
            id: "6.A.1.e",
            s: "1.e",
            desc: "Solve one-step inequalities with whole numbers using addition, subtraction, multiplication, and division and represent solutions on a number line (e.g., graph 3x > 3).",
          },
        ],
      },
      {
        id: "6.A.2",
        letter: "6.A.2",
        short: "Applications",
        standards: [
          {
            id: "6.A.2.a",
            s: "2.a",
            desc: "Create algebraic expressions (e.g., one operation, one variable as well as multiple operations, one variable) from word phrases.",
          },
          {
            id: "6.A.2.b",
            s: "2.b",
            desc: "Write equations (e.g., one operation, one variable) to represent authentic situations involving non-negative rational numbers.",
          },
          {
            id: "6.A.2.c",
            s: "2.c",
            desc: "Write inequalities (e.g., one operation, one variable) to represent authentic situations involving whole numbers.",
          },
        ],
      },
    ],
  },
  {
    id: "6.G",
    code: "G",
    name: "Geometry",
    grade: "6",
    clusters: [
      {
        id: "6.G.1",
        letter: "6.G.1",
        short: "Attributes",
        standards: [
          {
            id: "6.G.1.a",
            s: "1.a",
            desc: "Identify and create nets to represent two-dimensional drawings of prisms and pyramids.",
          },
        ],
      },
      {
        id: "6.G.2",
        letter: "6.G.2",
        short: "Coordinate Geometry",
        standards: [
          {
            id: "6.G.2",
            s: "2",
            desc: "Students will determine location, orientation, and relationships on the coordinate plane.",
          },
        ],
      },
      {
        id: "6.G.3",
        letter: "6.G.3",
        short: "Measurement",
        standards: [
          {
            id: "6.G.3.a",
            s: "3.a",
            desc: "Determine the area of quadrilaterals and triangles by composition and decomposition of these shapes, as well as applications of properties and formulas. Quadrilaterals include parallelograms and trapezoids.",
          },
          {
            id: "6.G.3.b",
            s: "3.b",
            desc: "Determine the surface area of rectangular prisms and triangular prisms using nets as well as application of formulas.",
          },
          {
            id: "6.G.3.c",
            s: "3.c",
            desc: "Apply volume formulas for triangular prisms.",
          },
        ],
      },
    ],
  },
  {
    id: "6.D",
    code: "D",
    name: "Data",
    grade: "6",
    clusters: [
      {
        id: "6.D.1",
        letter: "6.D.1",
        short: "Data Collection and Statistical Methods",
        standards: [
          {
            id: "6.D.1",
            s: "1",
            desc: "Students will formulate statistical investigative questions, collect data, and organize data.",
          },
        ],
      },
      {
        id: "6.D.2",
        letter: "6.D.2",
        short: "Analyze Data and Interpret Results",
        standards: [
          {
            id: "6.D.2.a",
            s: "2.a",
            desc: "Represent data using dot plots, box-and-whisker plots, and histograms.",
          },
          {
            id: "6.D.2.b",
            s: "2.b",
            desc: "Solve problems using information presented in dot plots, box-and-whisker plots, histograms, and circle graphs.",
          },
          {
            id: "6.D.2.c",
            s: "2.c",
            desc: "Find and interpret the mean, median, mode, and range for a set of data.",
          },
          {
            id: "6.D.2.d",
            s: "2.d",
            desc: "Compare the mean, median, mode, and range from two sets of data.",
          },
          {
            id: "6.D.2.e",
            s: "2.e",
            desc: "Compare and interpret data sets based upon their measures of central tendency and graphical representations (e.g., center, spread, shape).",
          },
        ],
      },
      {
        id: "6.D.3",
        letter: "6.D.3",
        short: "Probability",
        standards: [
          {
            id: "6.D.3.a",
            s: "3.a",
            desc: "Identify a list of possible outcomes for a simple event.",
          },
          {
            id: "6.D.3.b",
            s: "3.b",
            desc: "Describe the theoretical and experimental probability of an event using a fraction, percentage, and decimal.",
          },
          {
            id: "6.D.3.c",
            s: "3.c",
            desc: "Express the degree of likelihood (possible, impossible, certain, more likely, equally likely, or less likely) of simple events.",
          },
          {
            id: "6.D.3.d",
            s: "3.d",
            desc: "Compare and contrast theoretical and experimental probabilities.",
          },
        ],
      },
    ],
  },
  {
    id: "7.N",
    code: "N",
    name: "Number",
    grade: "7",
    clusters: [
      {
        id: "7.N.1",
        letter: "7.N.1",
        short: "Numeric Relationships",
        standards: [
          {
            id: "7.N.1",
            s: "1",
            desc: "Students will demonstrate, represent, and show relationships among rational numbers within the base-ten number system.",
          },
        ],
      },
      {
        id: "7.N.2",
        letter: "7.N.2",
        short: "Operations",
        standards: [
          {
            id: "7.N.2.a",
            s: "2.a",
            desc: "Add, subtract, multiply, and divide rational numbers (e.g., positive and negative fractions, decimals, and integers).",
          },
          {
            id: "7.N.2.b",
            s: "2.b",
            desc: "Apply properties of operations (commutative, associative, distributive, identity, inverse, zero) as strategies for problem solving with rational numbers.",
          },
        ],
      },
    ],
  },
  {
    id: "7.R",
    code: "R",
    name: "Ratios and Proportions",
    grade: "7",
    clusters: [
      {
        id: "7.R.1",
        letter: "7.R.1",
        short: "Proportional Relationships",
        standards: [
          {
            id: "7.R.1.a",
            s: "1.a",
            desc: "Decide whether two quantities are in a proportional relationship (e.g., by testing for equivalent ratios in a table).",
          },
          {
            id: "7.R.1.b",
            s: "1.b",
            desc: "Represent and solve authentic problems with proportions.",
          },
          {
            id: "7.R.1.c",
            s: "1.c",
            desc: "Use proportional relationships to solve authentic percent problems (e.g., percent change, sales tax, mark-up, discount, tip).",
          },
          {
            id: "7.R.1.d",
            s: "1.d",
            desc: "Solve authentic problems involving scale drawings.",
          },
        ],
      },
    ],
  },
  {
    id: "7.A",
    code: "A",
    name: "Algebra",
    grade: "7",
    clusters: [
      {
        id: "7.A.1",
        letter: "7.A.1",
        short: "Algebraic Processes",
        standards: [
          {
            id: "7.A.1.a",
            s: "1.a",
            desc: "Use factoring and properties of operations to create equivalent algebraic expressions (e.g., 2x + 6 = 2(x + 3)).",
          },
          {
            id: "7.A.1.b",
            s: "1.b",
            desc: "Given the value of the variable(s), evaluate algebraic expressions, which may include absolute value.",
          },
          {
            id: "7.A.1.c",
            s: "1.c",
            desc: "Solve one- and two-step equations involving rational numbers.",
          },
          {
            id: "7.A.1.d",
            s: "1.d",
            desc: "Solve equations using the distributive property and combining like terms.",
          },
          {
            id: "7.A.1.e",
            s: "1.e",
            desc: "Solve one- and two-step inequalities involving integers and represent solutions on a number line.",
          },
        ],
      },
      {
        id: "7.A.2",
        letter: "7.A.2",
        short: "Applications",
        standards: [
          {
            id: "7.A.2.a",
            s: "2.a",
            desc: "Write one- and two-step equations involving rational numbers from words, tables, and authentic situations.",
          },
          {
            id: "7.A.2.b",
            s: "2.b",
            desc: "Write one- and two-step inequalities to represent authentic situations involving integers.",
          },
        ],
      },
    ],
  },
  {
    id: "7.G",
    code: "G",
    name: "Geometry",
    grade: "7",
    clusters: [
      {
        id: "7.G.1",
        letter: "7.G.1",
        short: "Attributes",
        standards: [
          {
            id: "7.G.1.a",
            s: "1.a",
            desc: "Apply properties of adjacent, complementary, supplementary, linear pair, and vertical angles to find missing angle measures.",
          },
        ],
      },
      {
        id: "7.G.2",
        letter: "7.G.2",
        short: "Coordinate Geometry",
        standards: [
          {
            id: "7.G.2.a",
            s: "2.a",
            desc: "Draw polygons in the coordinate plane given coordinates for the vertices.",
          },
          {
            id: "7.G.2.b",
            s: "2.b",
            desc: "Calculate vertical and horizontal distances in the coordinate plane to find perimeter and area of rectangles.",
          },
        ],
      },
      {
        id: "7.G.3",
        letter: "7.G.3",
        short: "Measurement",
        standards: [
          {
            id: "7.G.3.a",
            s: "3.a",
            desc: "Solve authentic problems involving perimeter and area of composite shapes made from triangles and quadrilaterals.",
          },
          {
            id: "7.G.3.b",
            s: "3.b",
            desc: "Determine surface area and volume of composite rectangular and triangular prisms.",
          },
          {
            id: "7.G.3.c",
            s: "3.c",
            desc: "Determine the area and circumference of circles both on and off the coordinate plane using 3.14 for the value of Pi.",
          },
        ],
      },
    ],
  },
  {
    id: "7.D",
    code: "D",
    name: "Data",
    grade: "7",
    clusters: [
      {
        id: "7.D.1",
        letter: "7.D.1",
        short: "Data Collection and Statistical Methods",
        standards: [
          {
            id: "7.D.1.a",
            s: "1.a",
            desc: "Create an investigative question and collect data.",
          },
          {
            id: "7.D.1.b",
            s: "1.b",
            desc: "Generate conclusions about a population based on a random sample.",
          },
          {
            id: "7.D.1.c",
            s: "1.c",
            desc: "Identify and critique biases in various data representations.",
          },
        ],
      },
      {
        id: "7.D.2",
        letter: "7.D.2",
        short: "Analyze Data and Interpret Results",
        standards: [
          {
            id: "7.D.2",
            s: "2",
            desc: "Students will represent and analyze the data and interpret the results.",
          },
        ],
      },
      {
        id: "7.D.3",
        letter: "7.D.3",
        short: "Probability",
        standards: [
          {
            id: "7.D.3.a",
            s: "3.a",
            desc: "Find theoretical and experimental probabilities for compound independent and dependent events.",
          },
          {
            id: "7.D.3.b",
            s: "3.b",
            desc: "Identify complementary events and calculate their probabilities.",
          },
        ],
      },
    ],
  },
  {
    id: "8.N",
    code: "N",
    name: "Number",
    grade: "8",
    clusters: [
      {
        id: "8.N.1",
        letter: "8.N.1",
        short: "Numeric Relationships",
        standards: [
          {
            id: "8.N.1.a",
            s: "1.a",
            desc: "Determine subsets of numbers as natural, whole, integer, rational, irrational, or real based on the definitions of these sets of numbers.",
          },
          {
            id: "8.N.1.b",
            s: "1.b",
            desc: "Represent numbers with positive and negative exponents and in scientific notation.",
          },
          {
            id: "8.N.1.c",
            s: "1.c",
            desc: "Describe the difference between a rational and irrational number.",
          },
          {
            id: "8.N.1.d",
            s: "1.d",
            desc: "Approximate, compare, and order real numbers, both rational and irrational, and locate them on the number line.",
          },
        ],
      },
      {
        id: "8.N.2",
        letter: "8.N.2",
        short: "Operations",
        standards: [
          {
            id: "8.N.2.a",
            s: "2.a",
            desc: "Evaluate the square roots of perfect squares less than or equal to 400 and cube roots of perfect cubes less than or equal to 125.",
          },
          {
            id: "8.N.2.b",
            s: "2.b",
            desc: "Simplify numerical expressions involving integer exponents, square roots, and cube roots (e.g., 4⁻² is the same as 1/16).",
          },
          {
            id: "8.N.2.c",
            s: "2.c",
            desc: "Evaluate numerical expressions involving absolute value.",
          },
          {
            id: "8.N.2.d",
            s: "2.d",
            desc: "Multiply and divide numbers using scientific notation.",
          },
        ],
      },
    ],
  },
  {
    id: "8.A",
    code: "A",
    name: "Algebra",
    grade: "8",
    clusters: [
      {
        id: "8.A.1",
        letter: "8.A.1",
        short: "Algebraic Processes",
        standards: [
          {
            id: "8.A.1.a",
            s: "1.a",
            desc: "Describe single variable equations as having one solution, no solution, or infinitely many solutions.",
          },
          {
            id: "8.A.1.b",
            s: "1.b",
            desc: "Solve multi-step equations involving rational numbers with the same variable appearing on both sides of the equation.",
          },
          {
            id: "8.A.1.c",
            s: "1.c",
            desc: "Solve equations of the form x² = k (k ≤ 400) and x³ = k (k ≤ 125), where k is a positive rational number, using square root and cube root symbols.",
          },
        ],
      },
      {
        id: "8.A.2",
        letter: "8.A.2",
        short: "Applications",
        standards: [
          {
            id: "8.A.2.a",
            s: "2.a",
            desc: "Write multi-step single variable equations from words, tables, and authentic situations.",
          },
          {
            id: "8.A.2.b",
            s: "2.b",
            desc: "Determine and describe the rate of change for given situations through the use of tables and graphs.",
          },
          {
            id: "8.A.2.c",
            s: "2.c",
            desc: "Graph proportional relationships and interpret the rate of change.",
          },
        ],
      },
    ],
  },
  {
    id: "8.G",
    code: "G",
    name: "Geometry",
    grade: "8",
    clusters: [
      {
        id: "8.G.1",
        letter: "8.G.1",
        short: "Attributes",
        standards: [
          {
            id: "8.G.1.a",
            s: "1.a",
            desc: "Determine and use the relationships of the interior angles of a triangle to solve for missing measures.",
          },
          {
            id: "8.G.1.b",
            s: "1.b",
            desc: "Identify and apply geometric properties of parallel lines cut by a transversal and the resulting corresponding same side interior, alternate interior, and alternate exterior angles to find missing measures.",
          },
        ],
      },
      {
        id: "8.G.2",
        letter: "8.G.2",
        short: "Coordinate Geometry",
        standards: [
          {
            id: "8.G.2.a",
            s: "2.a",
            desc: "Perform and describe positions and orientations of shapes under single transformations including rotations in multiples of 90 degrees about the origin, translations, reflections, and dilations on and off the coordinate plane.",
          },
          {
            id: "8.G.2.b",
            s: "2.b",
            desc: "Determine if two-dimensional figures are congruent or similar.",
          },
          {
            id: "8.G.2.c",
            s: "2.c",
            desc: "Perform and describe positions and orientations of shapes under a sequence of transformations on and off the coordinate plane.",
          },
        ],
      },
      {
        id: "8.G.3",
        letter: "8.G.3",
        short: "Measurement",
        standards: [
          {
            id: "8.G.3.a",
            s: "3.a",
            desc: "Explain a model of the Pythagorean Theorem.",
          },
          {
            id: "8.G.3.b",
            s: "3.b",
            desc: "Apply the Pythagorean Theorem to find side lengths of triangles and to solve authentic problems.",
          },
          {
            id: "8.G.3.c",
            s: "3.c",
            desc: "Find the distance between any two points on the coordinate plane using the Pythagorean Theorem.",
          },
          {
            id: "8.G.3.d",
            s: "3.d",
            desc: "Determine the volume of cones, cylinders, and spheres and solve authentic problems using volumes.",
          },
        ],
      },
    ],
  },
  {
    id: "8.D",
    code: "D",
    name: "Data",
    grade: "8",
    clusters: [
      {
        id: "8.D.1",
        letter: "8.D.1",
        short: "Data Collection and Statistical Methods",
        standards: [
          {
            id: "8.D.1",
            s: "1",
            desc: "Students will formulate statistical investigative questions, collect data, and organize data.",
          },
        ],
      },
      {
        id: "8.D.2",
        letter: "8.D.2",
        short: "Analyze Data and Interpret Results",
        standards: [
          {
            id: "8.D.2.a",
            s: "2.a",
            desc: "Represent and interpret bivariate data (e.g., ordered pairs) using scatter plots.",
          },
          {
            id: "8.D.2.b",
            s: "2.b",
            desc: "Describe patterns such as positive or negative association, linear or nonlinear association, clustering, and outliers when bivariate data is represented on a coordinate plane.",
          },
          {
            id: "8.D.2.c",
            s: "2.c",
            desc: "Draw an informal line of best fit based on the closeness of the data points to the line.",
          },
          {
            id: "8.D.2.d",
            s: "2.d",
            desc: "Use a linear model to make predictions and interpret the rate of change and y-intercept in context.",
          },
        ],
      },
      {
        id: "8.D.3",
        letter: "8.D.3",
        short: "Probability",
        standards: [
          {
            id: "8.D.3",
            s: "3",
            desc: "Students will interpret and apply concepts of probability.",
          },
        ],
      },
    ],
  },
];
